import React, { Component, PropTypes } from 'react';
import idx from 'idx';
import { Form, Field, SubmissionError } from 'redux-form';
import {
  TextInputField, TextAreaField, PasswordInputField, RadioButtonField, FileSelectorField,
  ColorPickerField
} from 'common/components/form_fields';
import AnimatedButton from 'common/components/animated_button';
import VotePreview from 'presentations/components/vote_preview';
import IntroPreview from 'presentations/components/intro_preview';
import ModalDialog from 'common/components/modal/modal_dialog';
import { getPresentationDataFromForm } from 'presentations/utils';
import { STANDARD_PRIMARY_COLOR } from 'presentations/constants';

export default class Settings extends Component {
  constructor(props) {
    super(props);

    this.state = { votePreviewFull: false, introPreviewFull: false };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleFullVotePreview = this.toggleFullVotePreview.bind(this);
    this.toggleFullIntroPreview = this.toggleFullIntroPreview.bind(this);
  }

  toggleFullVotePreview() {
    this.setState({ votePreviewFull: !this.state.votePreviewFull });
  }

  toggleFullIntroPreview() {
    this.setState({ introPreviewFull: !this.state.introPreviewFull });
  }

  handleSubmit(formData) {
    const { update } = this.props;

    const presentationData = getPresentationDataFromForm(formData);

    return update(presentationData).catch(res => {
      /* field-specific errors */
      const data = res.data;
      if (data) {
        for (const field of Object.keys(data)) {
          if (data[field]) throw new SubmissionError({ [field]: data[field][0] });
        }
      }
      /* global error */
      throw new SubmissionError({ _error: 'Failed to update presentation. Please try again.' });
    });
  }

  renderVotePreview() {
    const { currentValues } = this.props;

    return (
      <VotePreview logo={idx(currentValues, _ => _.logo.preview)}
        primaryColor={
          currentValues.use_standard_colors? undefined : currentValues.custom_primary_color
        } />
    );
  }

  renderIntroPreview() {
    const { currentValues } = this.props;

    return (
      <IntroPreview logo={idx(currentValues, _ => _.logo.preview)}
        purpose={currentValues.purpose}
        primaryColor={
          currentValues.use_standard_colors? undefined : currentValues.custom_primary_color
        } />
    );
  }

  render() {
    const {
      handleSubmit, canUpdateWeightVotes, currentValues, submitting, submit, error, submitFailed
    } = this.props;
    const { votePreviewFull, introPreviewFull } = this.state;

    return (
      <div className="tab-content settings">
        <Form onSubmit={handleSubmit(this.handleSubmit)}>
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>Presentation Name</h3>
                <p>The name is only visible to you and your collaborators on crowdspring.</p>
                <Field name="name" label="Presentation Name" component={TextInputField} />
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div className="row">
              <div className="columns medium-6 small-16">
                <h3>Message to Participants</h3>
                <p>
                  Tell participants what you need them to do (i.e. "Please score these designs and comment
                  on what you like/don't like about each"). This message will appear at the top
                  of the gallery on the presentation site.
                </p>
                <Field name="purpose" component={TextAreaField} rows={5}/>
              </div>
              <div className="columns medium-10 small-16 preview-wrapper">
                <h3>Preview</h3>
                <ModalDialog modalOpen={introPreviewFull} closeAction={this.toggleFullIntroPreview}
                    modalHeader="Preview" dialogClasses="intro-preview-modal" contentClasses="wide"
                    onPrimary={this.toggleFullIntroPreview} primaryLabel="Done">
                  { this.renderIntroPreview() }
                </ModalDialog>
                { this.renderIntroPreview() }
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>URL</h3>
                <p>
                  Pick your custom subdomain (i.e. brightagency.pickfavorite.com).
                  Keep in mind that if you change the subdomain, you'll have to resend
                  invitations to everyone you already invited.
                </p>
                <Field name="sub_domain" component={TextInputField} preValue="https://"
                  postValue=".pickfavorite.com" />
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>Set a Presentation Site Password</h3>
                <p>
                  People must have a password to view your presentation site. We'll include the
                  password in the invitations to each participant.
                  Keep in mind that if you change the password, you'll have to resend
                  invitations to everyone you already invited.
                </p>
                <Field name="password" component={PasswordInputField} showVisibilityToggle={true} />
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>Share Results</h3>
                <p>Should participants see the results of the voting after participating?</p>
                <Field name="share_results" component={RadioButtonField}
                  props={{ value: true }}
                  label="Yes"
                />
                <Field name="share_results" component={RadioButtonField}
                  props={{ value: false }}
                  label="No"
                />
              </div>
            </div>
          </fieldset>
          { canUpdateWeightVotes &&
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>Weight Votes</h3>
                <p>Do you want some participants' votes count more than others?</p>
                <Field name="weight_votes" component={RadioButtonField}
                  props={{ value: true }}
                  label="Yes, use weighted votes."
                />
                <Field name="weight_votes" component={RadioButtonField}
                  props={{ value: false }}
                  label="No, all votes should be equal."
                />
              </div>
            </div>
          </fieldset> }
          <fieldset>
            <div className="row">
              <div className="columns medium-10">
                <h3>Allow Scoring/Comments?</h3>
                <Field name="permissions" component={RadioButtonField}
                  props={{ value: 'voting_and_comments' }}
                  label="Allow scoring & comments."
                />
                <Field name="permissions" component={RadioButtonField}
                  props={{ value: 'voting' }}
                  label="Allow scoring only."
                />
                <Field name="permissions" component={RadioButtonField}
                  props={{ value: 'comments' }}
                  label="Allow comments only."
                />
                <Field name="permissions" component={RadioButtonField}
                  props={{ value: 'nothing' }}
                  label="No scoring or comments."
                />
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div className="row">
              <div className="columns medium-6 small-16">
                <h3>Your Logo</h3>
                <p>500 X 500 square PNG or JPG</p>
                <Field name="logo" component={FileSelectorField}
                  acceptedFileType="image/jpg,image/jpeg,image/png" minImageWidth={400}
                  minImageHeight={400} withPreview={true} />

                <h3>Color Scheme</h3>
                <div className="standard-color-preview" style={{ background: STANDARD_PRIMARY_COLOR }} />
                <Field name="use_standard_colors" component={RadioButtonField}
                  props={{ value: true }}
                  label="Standard"
                />
                <Field name="use_standard_colors" component={RadioButtonField}
                  props={{ value: false }}
                  label="Custom"
                />
                { !currentValues.use_standard_colors &&
                <Field name="custom_primary_color" component={ColorPickerField} /> }
              </div>
              <div className="columns medium-10 small-16 preview-wrapper">
                <h3>Preview</h3>
                <ModalDialog modalOpen={votePreviewFull} closeAction={this.toggleFullVotePreview}
                    modalHeader="Preview" dialogClasses="vote-preview-modal" contentClasses="wide"
                    onPrimary={this.toggleFullVotePreview} primaryLabel="Done">
                  { this.renderVotePreview() }
                </ModalDialog>
                { this.renderVotePreview() }
              </div>
            </div>
          </fieldset>
        </Form>
        <AnimatedButton processing={submitting} onClick={submit}
          label="Save Changes" processingLabel="Saving..." errorMsg={error || submitFailed}
          successMsg="Saved"/>
      </div>
    );
  }
}

Settings.propTypes = {
  update: PropTypes.func.isRequired,
  canUpdateWeightVotes: PropTypes.bool.isRequired,
  currentValues: PropTypes.object.isRequired
};



// WEBPACK FOOTER //
// ./presentations/components/settings.jsx